import { supabase } from '../lib/supabase'

export interface TutorialVideo {
  id: string
  title: string
  description: string | null
  youtube_id: string
  sort_order: number
  is_welcome_video: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreateTutorialVideoInput {
  title: string
  description?: string
  youtube_id: string
  sort_order?: number
  is_welcome_video?: boolean
  is_active?: boolean
}

export interface UpdateTutorialVideoInput {
  title?: string
  description?: string
  youtube_id?: string
  sort_order?: number
  is_welcome_video?: boolean
  is_active?: boolean
}

/**
 * 公開中の動画一覧を取得（ユーザー向け）
 */
export async function getActiveTutorialVideos(): Promise<TutorialVideo[]> {
  const { data, error } = await supabase
    .from('tutorial_videos')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('Error fetching tutorial videos:', error)
    return []
  }

  return data || []
}

/**
 * ウェルカムモーダル用の動画を取得
 */
export async function getWelcomeVideo(): Promise<TutorialVideo | null> {
  const { data, error } = await supabase
    .from('tutorial_videos')
    .select('*')
    .eq('is_active', true)
    .eq('is_welcome_video', true)
    .order('sort_order', { ascending: true })
    .limit(1)
    .single()

  if (error) {
    // データがない場合はnullを返す
    if (error.code === 'PGRST116') {
      return null
    }
    console.error('Error fetching welcome video:', error)
    return null
  }

  return data
}

/**
 * 全動画一覧を取得（管理者向け、非公開も含む）
 */
export async function getAllTutorialVideos(): Promise<TutorialVideo[]> {
  const { data, error } = await supabase
    .from('tutorial_videos')
    .select('*')
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('Error fetching all tutorial videos:', error)
    return []
  }

  return data || []
}

/**
 * 動画を作成
 */
export async function createTutorialVideo(input: CreateTutorialVideoInput): Promise<TutorialVideo | null> {
  // 最大のsort_orderを取得
  const { data: maxOrderData } = await supabase
    .from('tutorial_videos')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .single()

  const nextOrder = (maxOrderData?.sort_order || 0) + 1

  const { data, error } = await supabase
    .from('tutorial_videos')
    .insert({
      title: input.title,
      description: input.description || null,
      youtube_id: input.youtube_id,
      sort_order: input.sort_order ?? nextOrder,
      is_welcome_video: input.is_welcome_video ?? false,
      is_active: input.is_active ?? true,
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating tutorial video:', error)
    throw error
  }

  return data
}

/**
 * 動画を更新
 */
export async function updateTutorialVideo(id: string, input: UpdateTutorialVideoInput): Promise<TutorialVideo | null> {
  const { data, error } = await supabase
    .from('tutorial_videos')
    .update(input)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating tutorial video:', error)
    throw error
  }

  return data
}

/**
 * 動画を削除
 */
export async function deleteTutorialVideo(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('tutorial_videos')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting tutorial video:', error)
    throw error
  }

  return true
}

/**
 * 動画の並び順を更新
 */
export async function updateVideoOrder(orderedIds: string[]): Promise<boolean> {
  try {
    // バッチ更新
    const updates = orderedIds.map((id, index) => ({
      id,
      sort_order: index + 1,
    }))

    for (const update of updates) {
      const { error } = await supabase
        .from('tutorial_videos')
        .update({ sort_order: update.sort_order })
        .eq('id', update.id)

      if (error) {
        console.error('Error updating video order:', error)
        throw error
      }
    }

    return true
  } catch (error) {
    console.error('Error updating video order:', error)
    throw error
  }
}

/**
 * ウェルカム動画を設定（他の動画のis_welcome_videoをfalseにする）
 */
export async function setWelcomeVideo(id: string): Promise<boolean> {
  try {
    // 全ての動画のis_welcome_videoをfalseに
    await supabase
      .from('tutorial_videos')
      .update({ is_welcome_video: false })
      .neq('id', id)

    // 指定した動画のis_welcome_videoをtrueに
    const { error } = await supabase
      .from('tutorial_videos')
      .update({ is_welcome_video: true })
      .eq('id', id)

    if (error) {
      throw error
    }

    return true
  } catch (error) {
    console.error('Error setting welcome video:', error)
    throw error
  }
}

/**
 * YouTubeのURLからIDを抽出
 */
export function extractYoutubeId(url: string): string | null {
  // 様々なYouTube URLフォーマットに対応
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/, // IDのみの場合
  ]

  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) {
      return match[1]
    }
  }

  return null
}
